<template>
  <div class="fleet-management">
    <h1>Edit fleets</h1>

    <b-row>
      <b-col cols="12">
        <b-card no-body class="">
          <!-- main content (table) -->
          <b-card-body class="card-content">
            <div>
              <b-row class="mb-4">
                <b-col cols="8">
                  <h3>My Fleets</h3>
                </b-col>
                <b-col cols="4" class="text-right">
                  <!-- input modal with a text input to enter a new fleet name -->
                  <input-message-box
                    @data-received="handleCreateFleet"
                    modal-title="New Fleet Details"
                    text-field-name="fleetName">

                    <template #action-button="buttonProps">
                      <button @click="buttonProps.showModal" class="btn btn-primary mx-2">
                        Create a new fleet
                      </button>
                    </template>
                  </input-message-box>
                </b-col>
              </b-row>

              <fleet-details-modal
                :fleet-id="currentEditFleetId"
                :fleets="fleets"
                @close-modal="handleFleetDetailsCloseModal()"/>

              <b-table :dark="darkMode"
                       :hover="true"
                       :striped="true"
                       :bordered="false"
                       :small="true"
                       :items="fleetsFormatted"
                       :fields="fields">

                <template v-if="isTableBusy" #top-row>
                  <b-td :colspan="fields.length">
                    <div class="text-center">
                      <i class="fa fa-2x fa-cog fa-spin text-muted m-2"></i>
                    </div>
                  </b-td>
                </template>

                <!-- rename fleet button -->
                <template #cell(rename)="cell">
                  <input-message-box
                    @data-received="data => handleRenameFleet(cell.item, data)"
                    :text-field-value="cell.item.fleetName"
                    modal-title="Rename fleet"
                    text-field-name="fleetName">

                    <template #action-button="buttonProps">
                      <div @click="buttonProps.showModal" class="edit-button rename-fleet"
                           :title="`Rename &quot;${cell.item.fleetName}&quot;`">
                        <i class="fa fa-lg fa-pencil"></i>
                      </div>
                    </template>
                  </input-message-box>
                </template>

                <!-- copy fleet button -->
                <template #cell(copy)="cell">
                  <div @click="handleFleetCopy(cell.item)" class="edit-button copy-fleet"
                       :title="`Edit &quot;${cell.item.fleetName}&quot;`">
                    <i class="fa fa-lg fa-copy"></i>
                  </div>
                </template>

                <!-- edit fleet button -->
                <template #cell(edit)="cell">
                  <div @click="handleFleetEdit(cell)" class="edit-button edit-fleet"
                       :title="`Edit &quot;${cell.item.fleetName}&quot;`">
                    <i class="fa fa-lg fa-edit"></i>
                  </div>
                </template>

                <!-- delete fleet button -->
                <template #cell(delete)="cell">
                  <div @click="handleFleetDelete(cell)" class="edit-button remove-fleet"
                       :title="`Remove &quot;${cell.item.fleetName}&quot;`">
                    <i class="fa fa-lg fa-remove"></i>
                  </div>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import getNested from 'lodash-es/get';
import { mapActions, mapGetters } from 'vuex';
import FleetDetailsModal from '../components/fleets/FleetDetailsModal';
import InputMessageBox from '../components/widgets/InputMessageBox';


export default {
  name: 'fleet-management',

  components: {
    InputMessageBox,
    FleetDetailsModal,
  },

  data() {
    return {
      fields: [
        {
          key: 'fleetName',
          label: 'Fleet Name',
        },
        {
          key: 'vesselsCount',
          label: 'Vessels Count',
          class: 'text-right',
          thClass: 'narrow-column',
        },
        {
          key: 'rename',
          label: 'Rename',
          class: 'text-center',
          thClass: 'narrow-column',
        },
        {
          key: 'copy',
          label: 'Copy',
          class: 'text-center',
          thClass: 'narrow-column',
        },
        {
          key: 'edit',
          label: 'Edit',
          class: 'text-center',
          thClass: 'narrow-column',
        },
        {
          key: 'delete',
          label: 'Delete',
          class: 'text-center',
          thClass: 'narrow-column',
        },
      ],
      fleets: [],
      currentEditFleetId: null,
      isTableBusy: true,
    };
  },

  async mounted() {
    await this.updateCurrentFleets();
  },

  methods: {
    ...mapActions('api/fleets', [
      'getFleets',
      'createFleet',
      'renameFleet',
      'copyFleet',
      'getFleet',
      'deleteFleet',
      'getAllVessels',
    ]),

    async handleCreateFleet(fleetData) {
      const apiData = {fleetName: fleetData.fleetName}
      await this.createFleet(apiData);
      await this.updateCurrentFleets();
    },

    async updateCurrentFleets() {
      // this.isTableBusy = true;
      let fleets = await this.getFleets();
      this.fleets = fleets.map(fleet => {
        return {
          ...fleet,
          vessels: fleet.vessels || [],
          vesselsCount: getNested(fleet, 'vesselsInFleet', 0),
          rename: '--',
          edit: '--',
          copy: '--',
          delete: '--',
        }
      });
      this.isTableBusy = false;
    },

    handleFleetEdit(cell) {
      this.currentEditFleetId = cell.item.fleetId;
    },

    async handleRenameFleet(origFleetData, {fleetName}) {
      let result = await this.renameFleet({
        fleetId: origFleetData.fleetId,
        fleetName,
      });

      if(result.error) {
        this.errors.push(result.error);
        return;
      }

      let currentFleet = this.fleets.find(fleet => fleet.fleetId === origFleetData.fleetId);
      currentFleet.fleetName = result.fleetName;
    },

    async handleFleetCopy(origFleetData) {
      const modalResult = await this.$bvModal.msgBoxConfirm(`Are you sure to copy the "${origFleetData.fleetName}" ?`, {
        title: 'Fleet copying',
        okVariant: 'info',
        okTitle: 'Copy',
        cancelTitle: 'Cancel',
        modalClass: 'fm-confirmation-modal',
        footerClass: 'p-2',
        bodyClass: 'confirmation-text',
        hideHeaderClose: false,
        centered: false,
      });

      if(modalResult) {
        let result = await this.copyFleet(origFleetData.fleetId);
        if(result.error) {
          this.errors.push(result.error);
          return;
        }

        await this.updateCurrentFleets();
      }
    },

    async handleFleetDelete(cell) {
      const modalResult = await this.$bvModal.msgBoxConfirm(`Are you sure to remove the "${cell.item.fleetName}" ?`, {
        title: 'Fleet removing confirmation',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'Cancel',
        modalClass: 'fm-confirmation-modal',
        footerClass: 'p-2',
        bodyClass: 'confirmation-text text-warning',
        hideHeaderClose: false,
        centered: false,
      });

      if(modalResult) {
        await this.deleteFleet(cell.item.fleetId);
        await this.updateCurrentFleets();
      }
    },

    async handleFleetDetailsCloseModal() {
      // all fleets data is refreshed at once
      await this.updateCurrentFleets();
      this.currentEditFleetId = null;
    },
  },

  computed: {
    ...mapGetters('api', [
      'getActiveRequestsCount',
    ]),

    ...mapGetters('gui', [
      'darkMode',
    ]),

    fleetsFormatted() {
      if(this.isTableBusy) {
        return [];
      } else {
        return this.fleets;
      }
    },
  },
};
</script>


<style>
.fm-confirmation-modal .confirmation-text {
  text-align: center;
  font-size: 16px;
}
</style>

<style scoped>
.fleet-management >>> .narrow-column {
  white-space: nowrap;
  width: 100px;
}
</style>

<style scoped lang='scss'>
.card-content {
  min-height: 300px;
  height: calc(100vh - 270px);
}

.edit-button {
  padding: 0 5px;
  cursor: pointer;
}

.remove-fleet {
  color: #a80000;

  &:hover {
    color: red;
  }
}

.copy-fleet {
  color: #2896db;

  &:hover {
    color: #78c0ff;
  }
}

.edit-fleet {
  color: #2896db;

  &:hover {
    color: #78c0ff;
  }
}

.rename-fleet {
  color: #2896db;

  &:hover {
    color: #78c0ff;
  }
}
</style>
